import {
  Button,
  FormField,
  IconButton,
  Spacer,
  SvgArrowLeft,
  SvgArrowRight,
  Text,
  TextInput,
} from '@loomhq/lens';
import _capitalize from 'lodash/capitalize';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import create from 'zustand';

import { showErrorBar } from 'actions/errorBar';
import { ERROR } from 'constants/error-severities';
import { COMPLETED_ONBOARDING_STEP } from 'constants/events';
import { SLACKID_ONBOARDING_MODAL } from 'constants/onboarding';
import { WORKSPACE_PLAN_BUSINESS } from 'constants/workspacePlans';
import { WORKSPACE_CREATION_METHOD_SIGNUP_FLOW } from 'constants/workspaces';
import * as analytics from 'utilities/analytics';
import { isPublicDomain, parseDomain } from 'utilities/email';
import { getGraphQLClient } from 'utilities/graphql';
import * as loggerx from 'utilities/loggerx';

import { selectCurrentUser } from 'selectors/user';

import { WORKSPACE_CREATE_STEP } from 'components/welcome-webapp-simplified/constants';
import AutoJoinSection from 'components/welcome-webapp-simplified/steps/workspace-create-step/AutoJoinSection';
import createWorkspace from 'components/welcome-webapp-simplified/steps/workspace-create-step/createWorkspace.graphql';
import { markSelfServeInviteAsAccepted } from 'components/welcome-webapp-simplified/utilities';

import styles from './styles.module.less';

export const useWorkspaceName = create(set => ({
  workspaceName: '',
  touched: false,
  setWorkspaceName: workspaceName => set({ workspaceName, touched: true }),
}));

function CreateWorkspaceStep({
  handleNextStep,
  handlePrevStep,
  foundSuggestedWorkspaces,
  requestedJoin,
  selfServeInvitePlan,
}) {
  const dispatch = useDispatch();

  const [defaultJoinRole, setDefaultJoinRole] = useState(null);
  const [autoJoinEnabled, setAutoJoinEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  let workspaceNamePlaceholder = '';
  const currentUser = useSelector(selectCurrentUser);
  const emailDomain = parseDomain(currentUser.email);

  const domainParsed = emailDomain?.split('.');
  const isWorkDomain = !isPublicDomain(emailDomain);

  // If the user has a gmail, yahoo, etc email domain,
  // use their first name for their workspace placeholder
  if (!isWorkDomain) {
    workspaceNamePlaceholder = `${currentUser.first_name}'s Workspace`;
  } else {
    // use their email domain as the placeholder (e.g. "loom" for "loom.com")
    workspaceNamePlaceholder = _capitalize(domainParsed?.[0]);
  }

  const inputElement = useRef();

  const { workspaceName, setWorkspaceName, touched } = useWorkspaceName();

  useEffect(() => {
    if (!touched) {
      setWorkspaceName(workspaceNamePlaceholder);
    }
  }, [setWorkspaceName, touched, workspaceName, workspaceNamePlaceholder]);

  useEffect(() => {
    if (inputElement.current) {
      inputElement.current.focus();
    }
  }, []);

  async function saveWorkspace() {
    setLoading(true);
    const client = getGraphQLClient();

    try {
      const {
        data: { newWorkspace },
      } = await client.mutate({
        mutation: createWorkspace,
        variables: {
          name: workspaceName,
          workspaceType: selfServeInvitePlan || WORKSPACE_PLAN_BUSINESS,
          creationMethod: WORKSPACE_CREATION_METHOD_SIGNUP_FLOW,
          autoJoinEnabled,
          defaultJoinRole,
        },
      });

      analytics.track(COMPLETED_ONBOARDING_STEP, {
        step: WORKSPACE_CREATE_STEP,
        organization_id: newWorkspace?.id,
        source: SLACKID_ONBOARDING_MODAL,
      });

      setLoading(false);

      await markSelfServeInviteAsAccepted(currentUser.email);

      handleNextStep();
    } catch (err) {
      loggerx.error(ERROR, 'Error creating new workspace');

      dispatch(
        showErrorBar(
          ERROR,
          'An error occurred attempting to create a Workspace'
        )
      );

      setLoading(false);
    }
  }

  function handleBack() {
    if (foundSuggestedWorkspaces) {
      // If found suggested workspaces, take user back to ability to join
      handlePrevStep();
    } else {
      // If there are no suggested workspaces, go back to persona/role step
      handlePrevStep(2);
    }
  }

  function renderHeader() {
    if (requestedJoin) {
      return (
        <>
          <Text size="large" fontWeight="bold">
            We sent your request!
          </Text>
          <Spacer bottom="medium" />
          <Text size="medium">
            We’ll email you when your request is approved. In the meantime, you
            can create a new, separate Workspace for yourself and the people you
            invite.
          </Text>
        </>
      );
    }

    return (
      <>
        <Text size="large" fontWeight="bold">
          Create a Workspace
        </Text>
        <Spacer bottom="medium" />
        <Text size="medium">
          A Workspace is where all videos can be accessed.
        </Text>
      </>
    );
  }

  return (
    <>
      {renderHeader()}
      <Spacer bottom="medium" />
      <FormField
        label="Workspace name"
        labelFor="name"
        className={styles.workspaceLabel}
      >
        <TextInput
          id="name"
          ref={inputElement}
          onChange={e => setWorkspaceName(e.target.value)}
          value={workspaceName}
          className={styles.workspaceText}
        />
      </FormField>

      {isWorkDomain && (
        <>
          <Spacer top="medium" />
          <AutoJoinSection
            domain={emailDomain}
            onChangeAutoJoinEnabled={setAutoJoinEnabled}
            onChangeDefaultJoinRole={setDefaultJoinRole}
            condensed
          />
        </>
      )}

      <Spacer bottom="medium" />
      <div className="flex justify:spaceBetween">
        <IconButton
          altText="back"
          icon={<SvgArrowLeft />}
          onClick={handleBack}
        />
        <Button
          isDisabled={workspaceName.trim() === '' || loading}
          icon={<SvgArrowRight />}
          iconPosition="right"
          onClick={saveWorkspace}
          hasLoader={loading}
        >
          Next
        </Button>
      </div>
    </>
  );
}

export default CreateWorkspaceStep;
